/**
 * Creates a debounced version of the provided function.
 * The debounced function delays invoking func until after wait milliseconds
 * have elapsed since the last time the debounced function was invoked.
 *
 * @param func The function to debounce
 * @param wait The number of milliseconds to delay
 * @param immediate If true, trigger the function on the leading edge instead of the trailing edge
 * @returns A debounced function that can be called repeatedly but will only execute once per wait period
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number = 300,
  immediate: boolean = false,
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function (this: unknown, ...args: Parameters<T>): void {
    // Capture the context for use in the later function
    const callFunc = (): void => {
      func.apply(this, args);
    };

    const later = (): void => {
      timeout = null;
      if (!immediate) callFunc();
    };

    const callNow = immediate && !timeout;

    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(later, wait);

    if (callNow) {
      callFunc();
    }
  };
}

/**
 * Creates a throttled version of the provided function.
 * The throttled function will only execute at most once every wait milliseconds.
 *
 * @param func The function to throttle
 * @param wait The number of milliseconds to throttle invocations to
 * @returns A throttled function that can only be executed once per wait period
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number = 300,
): (...args: Parameters<T>) => void {
  let lastCall = 0;

  return function (this: unknown, ...args: Parameters<T>): void {
    const now = Date.now();

    if (now - lastCall >= wait) {
      lastCall = now;
      func.apply(this, args);
    }
  };
}

/**
 * Returns a promise that resolves after the specified number of milliseconds.
 * Useful for adding delays in async/await code.
 *
 * @param ms The number of milliseconds to delay
 * @returns A promise that resolves after the delay
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
